// Porte das RPCs LGPD (supabase/migrations/20260902090400_rpc.sql, mantido
// no historico do git) para Firestore. SECURITY DEFINER não existe aqui —
// cada função abaixo faz as mesmas escritas que a RPC fazia, mas como
// operações comuns do cliente autenticado (ver firestore.rules).
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import type {
  ExcluirDadosTitularResultado,
  ExportarDadosTitularResultado,
  Lead,
  MarcarNaoContatarResultado,
  RevogarNaoContatarResultado,
} from "./types";

function uidAtual(): string | null {
  return auth.currentUser?.uid ?? null;
}

async function sha256Hex(texto: string): Promise<string> {
  const bytes = new TextEncoder().encode(texto);
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function registrarEventoLgpd(
  tipo: "exportacao" | "exclusao" | "supressao" | "revogacao_supressao",
  leadId: string | null,
  chave: string,
  observacao: string,
): Promise<void> {
  const chaveHash = chave.trim() === "" ? null : await sha256Hex(chave);
  await addDoc(collection(db, "lgpd_eventos"), {
    tipo,
    lead_id: leadId,
    chave_hash: chaveHash,
    observacao,
    executado_por: uidAtual(),
    executado_em: new Date().toISOString(),
  });
}

/** Mantém o lead e o histórico, cria a entrada de supressão e bloqueia
 * contato — porte de marcar_nao_contatar(). */
export async function marcarNaoContatar(leadId: string, motivo: string): Promise<MarcarNaoContatarResultado> {
  const motivoLimpo = motivo.trim();
  if (motivoLimpo === "") throw new Error("Motivo é obrigatório para registrar uma supressão.");

  const leadSnap = await getDoc(doc(db, "leads", leadId));
  if (!leadSnap.exists()) throw new Error(`Lead ${leadId} não encontrado.`);
  const lead = leadSnap.data() as Lead;

  // Grava as duas chaves disponíveis na mesma linha: o cruzamento em
  // estaSuprimido() é OR, entao uma linha já cobre reimportação tanto por
  // telefone quanto por nome+cidade.
  const ref = await addDoc(collection(db, "nao_contatar"), {
    telefone_normalizado: lead.telefone_normalizado,
    empresa_normalizada: lead.cidade_normalizada ? lead.empresa_normalizada : null,
    cidade_normalizada: lead.cidade_normalizada,
    motivo: motivoLimpo,
    origem_lead_id: leadId,
    marcado_por: uidAtual(),
    marcado_em: new Date().toISOString(),
    revogado_em: null,
    revogado_por: null,
    revogado_motivo: null,
  });

  await updateDoc(doc(db, "leads", leadId), { nao_contatar: true, canal_permitido: "nenhum" });

  const chave = `${lead.telefone_normalizado ?? ""}|${lead.empresa_normalizada ?? ""}|${lead.cidade_normalizada ?? ""}`;
  await registrarEventoLgpd("supressao", leadId, chave, motivoLimpo);

  return { lead_id: leadId, supressao_id: ref.id, ja_existia: false };
}

/** Desfaz uma supressão SEM apagar o registro dela (o histórico de que
 * existiu é parte da trilha de auditoria) — porte de revogar_nao_contatar(). */
export async function revogarNaoContatar(
  supressaoId: string,
  motivo: string,
): Promise<RevogarNaoContatarResultado> {
  const motivoLimpo = motivo.trim();
  if (motivoLimpo === "") throw new Error("Motivo é obrigatório para revogar uma supressão.");

  const supSnap = await getDoc(doc(db, "nao_contatar", supressaoId));
  if (!supSnap.exists() || supSnap.data().revogado_em) {
    throw new Error(`Supressão ${supressaoId} não encontrada ou já revogada.`);
  }
  const sup = supSnap.data() as {
    telefone_normalizado: string | null;
    empresa_normalizada: string | null;
    cidade_normalizada: string | null;
    origem_lead_id: string | null;
  };

  const revogadoEm = new Date().toISOString();
  await updateDoc(doc(db, "nao_contatar", supressaoId), {
    revogado_em: revogadoEm,
    revogado_por: uidAtual(),
    revogado_motivo: motivoLimpo,
  });

  // canal_permitido e "grudento" na direcao restritiva por seguranca:
  // precisa ser restaurado explicitamente, e so ate "somente_ligacao"
  // (WhatsApp continua exigindo resposta registrada).
  const leadsSnap = await getDocs(
    query(collection(db, "leads"), where("canal_permitido", "==", "nenhum"), where("nao_contatar", "==", false)),
  );
  const batch = writeBatch(db);
  let algumaAlteracao = false;
  for (const d of leadsSnap.docs) {
    const l = d.data() as Lead;
    if (l.etapa === "recusou") continue;
    const bateTelefone = sup.telefone_normalizado !== null && l.telefone_normalizado === sup.telefone_normalizado;
    const bateEmpresa =
      sup.empresa_normalizada !== null &&
      l.empresa_normalizada === sup.empresa_normalizada &&
      l.cidade_normalizada === sup.cidade_normalizada;
    if (bateTelefone || bateEmpresa) {
      batch.update(d.ref, { canal_permitido: "somente_ligacao" });
      algumaAlteracao = true;
    }
  }
  if (algumaAlteracao) await batch.commit();

  const chave = `${sup.telefone_normalizado ?? ""}|${sup.empresa_normalizada ?? ""}|${sup.cidade_normalizada ?? ""}`;
  await registrarEventoLgpd("revogacao_supressao", sup.origem_lead_id, chave, motivoLimpo);

  return { supressao_id: supressaoId, revogado_em: revogadoEm };
}

/** Responde a "me manda tudo que vocês têm sobre mim" — porte de
 * exportar_dados_titular(): lead + tentativas + histórico de etapas. */
export async function exportarDadosTitular(leadId: string): Promise<ExportarDadosTitularResultado> {
  const leadSnap = await getDoc(doc(db, "leads", leadId));
  if (!leadSnap.exists()) throw new Error(`Lead ${leadId} não encontrado.`);
  const lead = { id: leadId, ...(leadSnap.data() as Omit<Lead, "id">) };

  const [tentativasSnap, eventosSnap] = await Promise.all([
    getDocs(query(collection(db, "contact_attempts"), where("lead_id", "==", leadId))),
    getDocs(query(collection(db, "lead_stage_events"), where("lead_id", "==", leadId))),
  ]);

  const chave = `${lead.telefone_normalizado ?? ""}|${lead.empresa_normalizada ?? ""}|${lead.cidade_normalizada ?? ""}`;
  await registrarEventoLgpd("exportacao", leadId, chave, "Exportação de dados a pedido do titular");

  return {
    lead,
    tentativas: tentativasSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    historico_etapas: eventosSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    exportado_em: new Date().toISOString(),
  };
}

/** HARD DELETE — porte de excluir_dados_titular(): apaga lead + tentativas +
 * histórico de etapas, expurga o payload cru em import_items, e PRESERVA a
 * entrada de supressão (senão o titular volta a ser contatado na próxima
 * importação). Sem Cloud Function pra fazer isso como transação atômica no
 * servidor — ver aviso em firestore.rules sobre `leads` aceitar DELETE
 * direto só por causa deste caminho. */
export async function excluirDadosTitular(
  leadId: string,
  criarSupressao = true,
  motivo?: string,
): Promise<ExcluirDadosTitularResultado> {
  const leadSnap = await getDoc(doc(db, "leads", leadId));
  if (!leadSnap.exists()) throw new Error(`Lead ${leadId} não encontrado.`);
  const lead = leadSnap.data() as Lead;
  const motivoFinal = motivo?.trim() || "Pedido de exclusão do titular";

  const [tentativasSnap, eventosSnap, itemsSnap, itemsMatchSnap] = await Promise.all([
    getDocs(query(collection(db, "contact_attempts"), where("lead_id", "==", leadId))),
    getDocs(query(collection(db, "lead_stage_events"), where("lead_id", "==", leadId))),
    getDocs(query(collection(db, "import_items"), where("lead_id", "==", leadId))),
    getDocs(query(collection(db, "import_items"), where("match_lead_id", "==", leadId))),
  ]);

  const chave = `${lead.telefone_normalizado ?? ""}|${lead.empresa_normalizada ?? ""}|${lead.cidade_normalizada ?? ""}`;

  if (criarSupressao) {
    const jaExiste = await getDocs(
      query(
        collection(db, "nao_contatar"),
        where("telefone_normalizado", "==", lead.telefone_normalizado),
        where("revogado_em", "==", null),
      ),
    );
    if (lead.telefone_normalizado === null || jaExiste.empty) {
      await addDoc(collection(db, "nao_contatar"), {
        telefone_normalizado: lead.telefone_normalizado,
        empresa_normalizada: lead.cidade_normalizada ? lead.empresa_normalizada : null,
        cidade_normalizada: lead.cidade_normalizada,
        motivo: motivoFinal,
        origem_lead_id: null, // o lead vai deixar de existir
        marcado_por: uidAtual(),
        marcado_em: new Date().toISOString(),
        revogado_em: null,
        revogado_por: null,
        revogado_motivo: null,
      });
    }
  }

  const batch = writeBatch(db);
  for (const d of [...itemsSnap.docs, ...itemsMatchSnap.docs]) {
    batch.update(d.ref, {
      payload: { _expurgado: true, _expurgado_em: new Date().toISOString() },
      erro: "Payload expurgado a pedido do titular",
    });
  }
  for (const d of tentativasSnap.docs) batch.delete(d.ref);
  for (const d of eventosSnap.docs) batch.delete(d.ref);
  batch.delete(doc(db, "leads", leadId));
  await batch.commit();

  await registrarEventoLgpd("exclusao", leadId, chave, motivoFinal);

  return {
    lead_id: leadId,
    excluido_em: new Date().toISOString(),
    tentativas_excluidas: tentativasSnap.size,
    eventos_excluidos: eventosSnap.size,
    itens_import_expurgados: itemsSnap.size + itemsMatchSnap.size,
    supressao_preservada: criarSupressao,
    motivo: motivoFinal,
  };
}
